import {
  MAP_APP_LABELS,
  chosenMapApp,
  drives,
  formatCents,
  formatCount,
  hasLiveFiskalDocument,
  mapAppCaveat,
  tt,
  type MapApp,
} from '@agos/shared'

/* §9.751 - the door question, the receipt note and the toast name a method in the words the dialog's own
 * buttons already use (§9.609), so a driver reads *Hotovosť* in the note under the *Hotovosť* button. */
import { methodLabel } from '../wallet/wallet'

/**
 * The driving surface — what a driver holding a phone and a pizza bag needs on one screen.
 *
 * §9.6 retires the Expo driver app and moves drivers into the manager website. Its order of work is to port
 * the legacy web driver view (`pages/driver.tsx`, 354 lines) 1:1 first. Reading that file against the live
 * database is what produced **FB-180**, and it is why none of its wiring is carried over.
 *
 * **FB-180, in three parts.**
 *
 * 1. `driver.tsx` lists drivers with `employees.filter(e => e.role === "delivery" && e.active)`. There is no
 *    `role` column on `employees` — it is `system_role` — and no value `delivery`; the role is `driver`.
 *    Measured: **0** columns named `role`, and eleven drivers the screen therefore never shows.
 * 2. It matches orders with `o.employeeId === driverId`. On the 7 436 delivery orders `employee_id` is set
 *    on **0** and `driver_id` on **7 314** — `employee_id` is the waiter who took the order. The board was
 *    empty by construction.
 * 3. It has **no authentication**. The driver is whichever row was tapped on a public list, kept in
 *    `localStorage.driver_id`, and the delivery confirmation is posted with no Authorization header.
 *
 * So the surface has no driver picker. The signed-in employee is the driver, `GET /employees/my-deliveries`
 * is scoped to them by the server, and every write goes through the authenticated client.
 *
 * **And the wallet is a career, not a day.** `my-wallet` answers `walletTotal: 40 045,35 €` over 1 841
 * orders — everything one driver has ever delivered. Legacy's strip shows *today*. Printing the lifetime
 * figure where a driver reads *what I am carrying* is FB-101's mistake with a bigger number, so the two are
 * labelled apart and only the cash half is ever called money that gets handed over.
 */

// ─── The orders a driver is carrying ─────────────────────────────────────────

export interface DeliveryItem {
  productName: string
  quantity: number
}

export interface Delivery {
  id: number
  orderNumber: string
  status: string
  totalCents: number
  paymentMethod: string
  /**
   * The gateway's word on an online payment, or null - §9.751, FB-1066.
   *
   * `my-deliveries` has sent it all along and this file never read it, so a CardPay order the bank never
   * confirmed read *Nič sa nevyberá - zaplatené* on the driver's card while the dispatch board said *Platba
   * neúspešná* for the same order. Only `online_paid` means the bank took the money - the server's own
   * `OnlinePaymentService.alreadyPaid`.
   */
  paymentStatus: string | null
  /**
   * Whether the register holds a receipt for this order that nobody cancelled - §9.751, FB-1067.
   *
   * A live receipt is the lock that keeps the order's method at the close (§10A.304), so the door question has to
   * know whether one is out. Read once, here, through `@agos/shared`'s `hasLiveFiskalDocument` rather than a third
   * spelling of that predicate: the wire carries `fiskalPrintedAt` and `fiskalStornoAt` (§10A.391), and the fact
   * the screen needs is the one they make together.
   */
  liveReceipt: boolean
  customerName: string | null
  customerPhone: string | null
  customerAddress: string | null
  /**
   * Where the order actually is, when the geocoder resolved it - §9.504.
   *
   * The server has sent these on `my-deliveries` all along and this file dropped them, so every
   * *Navigovať* asked Google to guess the address text instead. 74 % of the owner's recent delivery
   * orders carry a point.
   */
  deliveryLat: number | null
  deliveryLon: number | null
  createdAt: string | null
  notes: string | null
  items: DeliveryItem[]
  /**
   * What an external courier is paid for this order, or null - §9.786, BACKEND.md §10A.438. The owner: *"apka musí
   * ukazovať aj odmenu externého šoféra"*. Null for the restaurant's own driver and for an order before couriers.
   */
  rewardCents: number | null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null
}

/**
 * A coordinate, or nothing - §9.504.
 *
 * `Number.isFinite` rather than a truthiness check on purpose: a latitude of `0` is a real place and a
 * `NaN` from a malformed payload is not, so the two must not be confused. Longitude 0 runs through
 * Greenwich, which is why neither is treated as absent.
 */
function coordinate(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

function count(value: unknown): number {
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.round(value) : 0
}

function euros(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return Math.round(value * 100)
  if (typeof value === 'string' && value !== '') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
  }
  return 0
}

export function deliveriesOf(payload: unknown): Delivery[] {
  if (!Array.isArray(payload)) return []
  return payload.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    const items = Array.isArray(row.items) ? row.items : []
    return [
      {
        id: row.id,
        orderNumber: text(row.orderNumber) ?? String(row.id),
        status: text(row.status) ?? '',
        totalCents: euros(row.total),
        paymentMethod: text(row.paymentMethod) ?? '',
        paymentStatus: text(row.paymentStatus),
        liveReceipt: hasLiveFiskalDocument({
          fiskalPrintedAt: text(row.fiskalPrintedAt),
          fiskalStornoAt: text(row.fiskalStornoAt),
        }),
        customerName: text(row.customerName),
        customerPhone: text(row.customerPhone),
        customerAddress: text(row.customerAddress),
        deliveryLat: coordinate(row.deliveryLat),
        deliveryLon: coordinate(row.deliveryLon),
        createdAt: text(row.createdAt),
        notes: text(row.notes),
        items: items.map((item) => {
          const line = item as Record<string, unknown>
          return { productName: text(line.productName) ?? '', quantity: count(line.quantity) }
        }),
        rewardCents: typeof row.driverReward === 'number' && Number.isFinite(row.driverReward) ? euros(row.driverReward) : null,
      },
    ]
  })
}

/**
 * Whether the customer pays the driver at the door.
 *
 * `cash` and `card_courier` are money the driver handles; a terminal or an online payment is already
 * settled. A driver needs to know which one is walking up the path with them.
 */
export function isPaidToDriver(paymentMethod: string): boolean {
  return paymentMethod === 'cash' || paymentMethod === 'card_courier'
}

/**
 * The methods the bank settles - the server's `PaymentMethods.BANK_SETTLED` (§10A.402), read here by §9.751.
 *
 * `card_online`, its legacy spelling `card`, and `qr_pay`: every handover takes these off the cash with nothing
 * typed, because the money went to the bank rather than to whoever stood at the door. **So whether one of them is
 * paid is the gateway's answer, never the method's** - and reading the method alone is FB-1066.
 *
 * Spelled exactly as `driver-payments.ts` spells it on *Platby online - archív*, so one cross-stack test can hold
 * both to the Java the same way. A list here rather than an import, because §5 keeps modules from importing each
 * other. Compared exactly, as the server compares `orders.payment_method`.
 */
export const BANK_SETTLED_METHODS = ['card_online', 'card', 'qr_pay'] as const

export function isBankSettled(method: string): boolean {
  return BANK_SETTLED_METHODS.some((settled) => settled === method)
}

/** The one status that says the bank took the money - `OnlinePaymentService.alreadyPaid`, §10A.262. */
const PAID_ONLINE = 'online_paid'

/** The status the gateway's refusal writes (§10A.244, §10A.303) - a payment that failed rather than one pending. */
const FAILED_ONLINE = 'online_failed'

/** The two methods a driver can be handed at a door - `isPaidToDriver`'s own two values. */
const DOOR_METHODS: readonly string[] = ['cash', 'card_courier']

/*
 * `paymentLabel` was here and is gone with §9.384, FB-683.
 *
 * It named the same six methods as the dispatch board, in different words and with no colour, and
 * the owner asked for the driver's badge to *"match colors from Rozvoz nástenka"*. That is one
 * vocabulary, not two: `paymentMethodBadge` moved into `@agos/shared` and the card reads it. Left as
 * a note rather than silently deleted, because the next person to want a payment word here should
 * find the shared one instead of writing a third.
 */

/**
 * What is owed at this door - §9.610, FB-917.
 *
 * **Three outcomes because two facts were sharing one `null`.** *Settled elsewhere* and *nobody has said* are
 * different things and only the first is good news: `collectCents` answered `null` for both, and
 * `collectLabel` turned that into *"Nič sa nevyberá - zaplatené"*. **A blank method means nobody said**, so
 * the one state where a driver most needs to ask was the state the card answered with a positive claim.
 *
 * **Exhaustive at every reader, with no `default`**, so a fourth outcome is a compile error rather than a
 * sentence somebody has to remember to add - §9.507's guarantee, and §9.512 is where it paid: a fifth map app
 * became a TS2366 instead of a Google link under a Mapy label.
 *
 * **And a fourth since §9.751, FB-1066: `unconfirmed`.** A method the bank settles (`BANK_SETTLED_METHODS`) is
 * settled only once the gateway says `online_paid`; read off the method alone, a CardPay order the bank never
 * confirmed - order 15897 at Šurany, with no gateway session at all - told the driver *zaplatené* while the
 * dispatch board showed *Platba neúspešná* for the same order. It carries the total, because the driver may yet
 * have to take it, and whether the bank **refused** it (`online_failed`) rather than merely not answering, because
 * those are two different things to tell a driver. The guarantee above is what made it cheap: every reader that
 * switches over the outcome failed to compile until it said something about this one.
 */
export type CollectOutcome =
  | { kind: 'collect'; cents: number }
  | { kind: 'settled' }
  | { kind: 'unconfirmed'; cents: number; failed: boolean }
  | { kind: 'unknown' }
  | { kind: 'qr'; cents: number }

/**
 * The method a customer's *QR platba* is filed as - §9.766, owner O127, on BACKEND.md §10A.418. The same value a paid
 * door transfer is filed as (§10A.390), so `paymentStatus` is what tells *pays at this door* from *paid*.
 */
export const QR_PAY = 'qr_pay'

/**
 * Whether the customer pays this order at the door by the QR the driver shows - §9.766.
 *
 * **A fifth outcome, `qr`, since the customer may choose QR at checkout** (BACKEND.md §10A.418). Read as
 * `unconfirmed` - the bank-settled rule above - it told the driver to ask the dispatcher whether to take the money,
 * about an order whose whole plan is that the driver shows a code and the customer pays it here. The money goes to the
 * bank, so it is not the driver's to collect (`collectCents` answers null) - but it is owed at this door, and the card
 * says so. Paid (`online_paid`) it is `settled`, as every bank-settled order the bank confirmed is.
 */
export function paysByQrAtTheDoor(delivery: Pick<Delivery, 'paymentMethod' | 'paymentStatus'>): boolean {
  return delivery.paymentMethod === QR_PAY && delivery.paymentStatus !== PAID_ONLINE
}

export function collectOutcome(delivery: Delivery): CollectOutcome {
  if (isPaidToDriver(delivery.paymentMethod)) {
    return { kind: 'collect', cents: delivery.totalCents }
  }
  if (delivery.paymentMethod.trim() === '') return { kind: 'unknown' }
  if (paysByQrAtTheDoor(delivery)) return { kind: 'qr', cents: delivery.totalCents }
  if (isBankSettled(delivery.paymentMethod) && delivery.paymentStatus !== PAID_ONLINE) {
    return {
      kind: 'unconfirmed',
      cents: delivery.totalCents,
      failed: delivery.paymentStatus === FAILED_ONLINE,
    }
  }
  return { kind: 'settled' }
}

/**
 * What the driver expects to collect, for the run's total - or nothing.
 *
 * **An `unknown` deliberately answers `null` here, and that is not the conflation §9.610 fixed.** This feeds
 * `runTotals`' forecast, and an order nobody has priced a method for is **not known to be collectable** - so
 * counting it would forecast money that may already be paid. What changed is that the card no longer claims
 * such an order is settled, which is the half that could mislead somebody at a door.
 *
 * **`unconfirmed` answers `null` too, for the same reason** (§9.751): the bank may yet confirm it, so it is not
 * known to be the driver's to collect - and the card says so with a sentence instead.
 */
export function collectCents(delivery: Delivery): number | null {
  const outcome = collectOutcome(delivery)
  return outcome.kind === 'collect' ? outcome.cents : null
}

/**
 * The methods a driver can be handed at a door - §9.609, FB-916.
 *
 * **`isPaidToDriver`'s own two values rather than a new list**, so the question and the *Vyberte 24,50* line
 * above it cannot come to disagree: that predicate is what decides whether there is money to collect, and
 * these are the only two things that money can be.
 *
 * **Empty for an order that is already settled**, which is what stops the question being asked at all. A
 * driver relabelling a `card_online` order would overwrite a fact the gateway established, and §10A.304 is
 * the entry where widening a payment-method rule past the evidence produced the owner's next complaint
 * within the hour.
 *
 * **An `unconfirmed` order is asked about, with its own online method first** - §9.751, FB-1066. The bank has
 * not said the money is in, so the driver may have taken it at the door; the order's own method is the third
 * answer, *I took nothing*, and choosing it sends no method at all (`deliveredPayload`), so the gateway's fact is
 * never overwritten by the driver's say-so. That is the exception the paragraph above draws: nothing here
 * relabels a payment the bank *established*, only one it never confirmed.
 */
export function doorPaymentChoices(delivery: Delivery): readonly string[] {
  const outcome = collectOutcome(delivery)
  /* §9.610 - *not settled* rather than *paid to the driver*, so a method nobody has set is asked about too.
   * Gated on `isPaidToDriver`, a blank got no question either: the order closed with nothing recorded and the
   * driver was told there was nothing to take. No `default`, so a fifth outcome is TS2366 here as well. */
  switch (outcome.kind) {
    case 'settled':
      return []
    case 'unconfirmed':
      return [delivery.paymentMethod, ...DOOR_METHODS]
    case 'collect':
    case 'unknown':
      return DOOR_METHODS
    /* §9.766 - a customer who chose QR and paid another way. A QR the bank pays closes the order itself (the sheet's
     * poll, §10A.127), so *Doručené* on such an order means the money came some other way, and these two are the
     * ways it can: the server writes either over an unpaid `qr_pay` (BACKEND.md §10A.418 point 6). `qr_pay` is not an
     * answer here - nothing the driver says makes the bank have the money. */
    case 'qr':
      return DOOR_METHODS
  }
}

/**
 * What the door question opens on - §9.609, and §9.751's correction of it.
 *
 * **The customer's own declaration where it is one of the door's two methods**, so a customer who does what
 * they said costs the driver one tap (§9.609; `card_courier` is 46 % of this franchise's deliveries).
 *
 * **Nothing for an `unconfirmed` order**, which is §9.579's rule: a dialog must not open on a value nobody chose
 * and send it as a choice. The page seeded the dialog with the order's own method, and for a CardPay order the
 * bank never confirmed that method is now one of the answers - so it would open already saying *I took
 * nothing*, the one answer that question exists to make somebody give on purpose. Nothing for `unknown`, which
 * has no declaration to open on, and nothing for `settled`, which is not asked. No `default`.
 */
export function doorPaymentPreselect(delivery: Delivery): string {
  const outcome = collectOutcome(delivery)
  switch (outcome.kind) {
    case 'collect':
      return delivery.paymentMethod
    // §9.766 - `qr` on neither: the customer chose QR, so cash or the card is something they said at the door.
    case 'qr':
    case 'unconfirmed':
    case 'unknown':
    case 'settled':
      return ''
  }
}

/**
 * A door choice's words - §9.609's labels, and §9.751's for the order's own online method.
 *
 * **The online method says the driver takes nothing**, because on that button it is not a way the customer
 * paid at the door - it is the driver saying the bank has the money. *Online* alone would read as a third way to
 * be paid at the door, which is the misreading FB-1066 is about.
 */
export function doorChoiceLabel(method: string): string {
  return isBankSettled(method) ? `${methodLabel(method)} - ${tt('nič nevyberám')}` : methodLabel(method)
}

/**
 * What *Doručené* puts on the wire - §9.609.
 *
 * **`paymentMethod` is omitted rather than sent unchanged** when the order is not paid at the door. A key the
 * request does not carry cannot be written by a rule somebody changes later, which is a stronger guarantee
 * than sending the value it already has and trusting the server to notice.
 *
 * **`confirm-status` has accepted this field all along** - `OrderStatusService.confirm` hands it to `settle`,
 * which writes it through `nextPaymentMethod`, and that is where §10A.304's lock lives. So this sends a
 * request the server has always understood and never been asked.
 *
 * **And never a method the bank settles** - §9.751. Choosing an `unconfirmed` order's own online method is the
 * driver saying they took nothing, and that is said by sending no method: the close keeps the stored one, as it
 * does with no question at all. A bank-settled method on the wire is one §10A.402 keeps out anyway, with a
 * warning in the log naming the order - so sending it would be a request the server is built to refuse.
 */
export function deliveredPayload(
  delivery: Delivery,
  chosen: string,
): { newStatus: string; reason: string; paymentMethod?: string } {
  const base = { newStatus: 'delivered', reason: tt('Doručené šoférom') }
  if (!doorPaymentChoices(delivery).includes(chosen)) return base
  if (isBankSettled(chosen)) return base
  return { ...base, paymentMethod: chosen }
}

/**
 * What the driver is told under the door question when one of §10A.304's two locks will keep the method - §9.751,
 * FB-1067.
 *
 * **A live receipt** keeps the method it was printed with, because the document says what it says until somebody
 * stornoes it, and a driver at a door cannot storno. Nesvady prints 77 of its 79 cash deliveries before the driver
 * reaches the door, so a card taken there was dropped with nothing said to anybody. **A payment the bank already
 * took** keeps its order's method too - the state behind owner question #90, an order filed for the door that the
 * gateway has been paid for. The server raises `door_method_kept` for the staff on either (§10A.405); this is the
 * driver's half, said **before** the tap rather than discovered after it.
 *
 * **The bank before the receipt**, because the server names them in that order (`lockOn`) and asks the staff for a
 * different correction on each: the bank's sentence must not promise a storno nobody will be asked for.
 *
 * Said only for a choice the question actually offers that differs from the order's own method - choosing what the
 * order already says changes nothing and needs no warning - and a stornoed receipt locks nothing (`liveReceipt`).
 */
export function choiceKeptNote(delivery: Delivery, chosen: string): string {
  if (!doorPaymentChoices(delivery).includes(chosen)) return ''
  if (chosen === delivery.paymentMethod) return ''
  if (delivery.paymentStatus === PAID_ONLINE) {
    return tt('Banka platbu za túto objednávku už potvrdila - zmena sa neuloží, personál dostane upozornenie.')
  }
  if (!delivery.liveReceipt) return ''
  return `${tt('Doklad je vytlačený ako')} ${methodLabel(delivery.paymentMethod)}. ${tt('Zmena sa sama neuloží - personál dostane upozornenie, aby spôsob platby opravil.')}`
}

/**
 * The two words `OrderStatusConfirmed.methodKeptBy` carries - BACKEND.md §10A.405's `MethodLock`, which a
 * cross-stack test holds this list to. The answer names one exactly when the close told the staff.
 */
export const METHOD_KEPT_BY = ['receipt', 'bank'] as const

export type MethodKeptBy = (typeof METHOD_KEPT_BY)[number]

/**
 * Why the close kept the door's answer out **and told the staff**, off its answer - or null. Read as defensively as
 * `keptMethodOf`, for its reason, and **absent is not a reason**: a method that differs with no word here was kept
 * with nobody told (a second press on an order already delivered), and the toast must not say they were.
 */
export function keptByOf(answer: unknown): MethodKeptBy | null {
  if (typeof answer !== 'object' || answer === null) return null
  const why = (answer as { methodKeptBy?: unknown }).methodKeptBy
  return METHOD_KEPT_BY.find((known) => known === why) ?? null
}

/**
 * The method the order holds after the close, off its answer - §9.751, on BACKEND.md §10A.405.
 *
 * **Read defensively rather than through the generated type**, which predates the field: `OrderStatusConfirmed`
 * gains `paymentMethod` on the server and the committed spec has not been re-exported, so the type says nothing
 * about it. `mapAppOf`'s shape, for `mapAppOf`'s reason.
 *
 * **Absent is not false.** A server older than §10A.405, or a stub that answers `{ ok: true }`, carries no field,
 * and that must read as *nothing to say* rather than as *the method changed* - so anything but a non-empty string
 * is `null`.
 */
export function keptMethodOf(answer: unknown): string | null {
  if (typeof answer !== 'object' || answer === null) return null
  const kept = (answer as { paymentMethod?: unknown }).paymentMethod
  return typeof kept === 'string' && kept.trim() !== '' ? kept : null
}

/**
 * The toast after *Doručené* - §9.751, FB-1067.
 *
 * *Doručené* was all it ever said, so a choice the server kept out was dropped in silence. **It says which
 * method stayed only when the driver sent one and the answer names a different one**: a close that sent no
 * method (a settled order, the online answer, a hand-back) asked for nothing, and an answer without the field
 * says nothing either way. Held twelve seconds, as the fiskal storno warnings are (`use-toast.ts`), because a
 * sentence about money read at a door must not vanish at Radix's default of five - the `Toaster` sets none.
 */
export function deliveredToast(
  sent: string | undefined,
  answer: unknown,
): { title: string; description?: string; duration?: number } {
  const title = tt('Doručené')
  if (sent === undefined) return { title }
  const kept = keptMethodOf(answer)
  if (kept === null || kept === sent) return { title }
  const stayed = `${tt('Spôsob platby zostal')} ${methodLabel(kept)}`
  /* §10A.405's `methodKeptBy` - the staff are said to have been told only when the close says it told them, and
   * the correction is named by its reason: a receipt is stornoed and printed again, a payment the bank already took
   * is returned if the customer also paid at the door. With no word, which method stayed is all there is to say. */
  const why = keptByOf(answer)
  if (why === null) return { title, description: `${stayed}.`, duration: 12_000 }
  switch (why) {
    case 'receipt':
      return {
        title,
        description: `${stayed} - ${tt('doklad je už vytlačený, personál dostal upozornenie, aby ho opravil.')}`,
        duration: 12_000,
      }
    case 'bank':
      return {
        title,
        description: `${stayed} - ${tt('banka platbu už potvrdila, personál dostal upozornenie.')}`,
        duration: 12_000,
      }
  }
}

/**
 * The toast after either close the card offers - *Doručené* or *Vrátiť späť* - §9.751, FB-1069.
 *
 * **The hand-back shares the close's mutation** (§9.370), so its success read *Doručené* too - a false sentence to
 * a driver who has just carried the food back to the car. Decided by what was sent rather than by which dialog was
 * open, because the request is the fact: anything but `delivered` is the hand-back's `quality_check`.
 */
export function closeToast(
  sent: { newStatus: string; paymentMethod?: string },
  answer: unknown,
): { title: string; description?: string; duration?: number } {
  if (sent.newStatus !== 'delivered') return { title: tt('Vrátené späť') }
  return deliveredToast(sent.paymentMethod, answer)
}

/**
 * The folded card's word for an `unconfirmed` payment, or '' for every other outcome - §9.751, the review's catch.
 *
 * Cards start folded (§9.387), and the *zaplatené* chip was a folded card's only word on payment. It is gone for
 * an unconfirmed order, which left the header's blue *CardPay online* badge - the tone of money already in the bank
 * - as the only thing a driver scanning the run would see. The amber word sits where the chip was.
 */
export function unconfirmedChip(outcome: CollectOutcome): string {
  switch (outcome.kind) {
    case 'unconfirmed':
      return outcome.failed ? tt('platba zlyhala') : tt('nepotvrdené')
    // §9.766 - `qr` has `qrChip`, under its own marker: it is not a payment anybody is unsure of.
    case 'qr':
    case 'collect':
    case 'settled':
    case 'unknown':
      return ''
  }
}

/**
 * The folded card's word for a customer who pays by QR at this door, or '' - §9.766. The header badge reads *QR Pay*
 * in the paid-online blue whatever the status, so a folded card needs this word to say the money is not in yet.
 */
export function qrChip(outcome: CollectOutcome): string {
  return outcome.kind === 'qr' ? tt('platí QR') : ''
}

export function collectLabel(delivery: Delivery): string {
  const outcome = collectOutcome(delivery)
  /* No `default`, so a fourth outcome is TS2366 here rather than a door with no sentence on it. */
  switch (outcome.kind) {
    case 'collect':
      return `${tt('Vyberte')} ${formatCents(outcome.cents)}`
    case 'settled':
      return tt('Nič sa nevyberá - zaplatené')
    case 'unconfirmed':
      /* §9.751 - the dispatcher can see what the gateway said and the driver cannot, so the sentence sends the
       * driver there, with the amount they may have to take. A refusal is said as one: *zlyhala* is a different
       * thing to hear at a door from *nie je potvrdená*. */
      return outcome.failed
        ? `${tt('Platba online zlyhala - zistite u dispečera, či vybrať')} ${formatCents(outcome.cents)}`
        : `${tt('Platba online nie je potvrdená - zistite u dispečera, či vybrať')} ${formatCents(outcome.cents)}`
    case 'unknown':
      return tt('Neuvedená platba - zistite u dispečera')
    case 'qr':
      // §9.766 - what to take and how: the amount, and that it is paid by the code the driver shows.
      return `${tt('Platí QR kódom')} ${formatCents(outcome.cents)} - ${tt('ukážte zákazníkovi QR')}`
  }
}

/**
 * The collect badge's colour - §9.751.
 *
 * **It was a nested ternary on the page that ended in blue**, the colour of *paid*, for every outcome it did not
 * name - so an `unconfirmed` order would have drawn as paid with no compiler to say so. An exhaustive `switch`
 * with no `default` makes a fifth outcome a TS2366 instead. Amber for both outcomes that send the driver to the
 * dispatcher, because neither is money in hand nor money already in. The classes are the ternary's own.
 */
export function collectTone(outcome: CollectOutcome): string {
  switch (outcome.kind) {
    // §9.766 - `qr` is money owed at this door, as a cash order's is: the green of *take this here*.
    case 'qr':
    case 'collect':
      return 'bg-emerald-500/15 text-emerald-600'
    case 'unknown':
    case 'unconfirmed':
      return 'bg-amber-500/15 text-amber-600'
    case 'settled':
      return 'bg-blue-500/15 text-blue-500'
  }
}

export function statusLabel(status: string): string {
  if (status === 'on_the_way') return tt('Na ceste')
  // §9.707 - the driver's own *Ísť na adresu* writes this; nobody is at the door yet.
  if (status === 'at_address') return tt('Ide na adresu')
  if (status === 'ready') return tt('Pripravená na vyzdvihnutie')
  if (status === 'delivered') return tt('Doručená')
  return status
}

/** How long the order has been waiting, in the bands §14.8 uses everywhere else. */
export type Urgency = 'fresh' | 'watch' | 'late'

export function minutesWaiting(delivery: Delivery, now: number): number {
  if (delivery.createdAt === null) return 0
  const stamp = Date.parse(delivery.createdAt)
  if (Number.isNaN(stamp) || stamp > now) return 0
  return Math.floor((now - stamp) / 60_000)
}

export function urgencyOf(minutes: number): Urgency {
  if (minutes >= 60) return 'late'
  if (minutes >= 30) return 'watch'
  return 'fresh'
}

export function elapsedLabel(minutes: number): string {
  if (minutes < 60) return `${formatCount(minutes)} min`
  return `${formatCount(Math.floor(minutes / 60))} h ${formatCount(minutes % 60)} min`
}

/** Oldest first: the one that has been waiting longest is the one to worry about. */
export function orderedDeliveries(deliveries: readonly Delivery[]): Delivery[] {
  return [...deliveries].sort((left, right) => (left.createdAt ?? '').localeCompare(right.createdAt ?? ''))
}

/**
 * What is on the driver's phone right now, from the deliveries themselves.
 *
 * **`alreadyPaid` counts `settled` alone - §9.751.** It was every order not paid to the driver, so an order the
 * bank never confirmed, and one nobody had priced, both counted as *Zaplatené vopred*. The block that draws it is
 * hidden since §9.384, and that is not a reason to leave it false for the day it comes back. `collectCents` is
 * unchanged: an `unconfirmed` order may yet be paid, so it is not money to collect either.
 */
export function runTotals(deliveries: readonly Delivery[]): {
  stops: number
  collectCents: number
  alreadyPaid: number
} {
  const collecting = deliveries.filter((delivery) => isPaidToDriver(delivery.paymentMethod))
  return {
    stops: deliveries.length,
    // A plain integer reduce: `sumCents` parses its inputs and would multiply cents by a hundred.
    collectCents: collecting.reduce((total, delivery) => total + delivery.totalCents, 0),
    alreadyPaid: deliveries.filter((delivery) => collectOutcome(delivery).kind === 'settled').length,
  }
}

// ─── Navigation and the phone ────────────────────────────────────────────────

/** What a navigation link can be aimed at: a resolved point, an address, or neither. */
export interface NavigationTarget {
  customerAddress: string | null
  deliveryLat: number | null
  deliveryLon: number | null
}

/**
 * Where *navigate* goes.
 *
 * The RN app opens `google.navigation:q=…`, an **Android intent** with no web equivalent. The universal
 * Maps directions URL works on every platform and is what the app itself falls back to, so it is the only
 * one here - a link that does nothing on an iPhone is worse than one that opens a map.
 *
 * **The coordinate wins where there is one - §9.504, owner report FB-794.** This read the address text and
 * let Google resolve it at every stop, which is the owner's *"every second/third address is navigating to
 * wrong address"*: 24 % of their addresses carry the Slovak descriptive/orientation number (*Hlavná
 * 1234/12*), and Google frequently reads the descriptive half as the house number. A point has nothing to
 * misread.
 *
 * **The text path stays** because a third of orders have no resolved point, and a map opened at a searched
 * address beats a dead button.
 *
 * **The pair is NOT URL-encoded.** `encodeURIComponent` would make the comma `%2C`, which `destination`
 * treats as text rather than as coordinates - so encoding it would quietly turn this fix back into a search.
 */
/**
 * The four apps and the value-to-app rule now live in `@agos/shared` - §9.509.
 *
 * **Moved and re-exported rather than relocated outright.** Three surfaces need the list: this one builds the link,
 * the delivery settings choose the franchise default and the employee editor chooses a driver's own, and §5 forbids
 * `manager-delivery` importing this module. §9.500 did the same for `isTransientKasaWarning` and for the same
 * reason - re-exported so this file's existing assertions on `MAP_APPS` are untouched, since a moved value whose
 * tests were rewritten beside it proves nothing about the move.
 */
export { MAP_APPS } from '@agos/shared'
export type { MapApp } from '@agos/shared'

/**
 * The link for one app and one resolved point - §9.507.
 *
 * **Sygic takes longitude first.** `com.sygic.aura://coordinate|LON|LAT|drive` is the documented order, and
 * copying the Google branch would send a driver to the mirror of the right place - which is hard to spot in
 * Slovakia, where latitudes are about 48 and longitudes about 18. Hence its own case.
 *
 * **Sygic is also the one custom scheme**, so its button does nothing on a phone without the app. That is why it
 * is a franchise's deliberate choice and never a default - §9.507 has the reasoning.
 *
 * **No `encodeURIComponent` on the pair.** §9.504: the comma becomes `%2C` and every one of these parameters
 * then reads it as text rather than as coordinates.
 *
 * **And deliberately no `default`.** With one, an app added to {@link MAP_APPS} and forgotten here would
 * silently open Google while the setting said Waze - §9.502's shape, a value the writer knows and the reader
 * does not. Without one the switch is exhaustive over {@link MapApp}, so forgetting it is a **compile error**.
 * That is also what makes {@link chosenApp} the single place an unrecognised value becomes `google`: a mutation
 * removing its membership check used to be invisible, because the `default` was a second spelling of the same
 * fallback.
 */
function pointUrl(app: MapApp, lat: number, lon: number): string {
  switch (app) {
    case 'apple':
      return `https://maps.apple.com/?daddr=${lat},${lon}&dirflg=d`
    case 'waze':
      return `https://waze.com/ul?ll=${lat},${lon}&navigate=yes`
    case 'sygic':
      return `com.sygic.aura://coordinate|${lon}|${lat}|drive`
    case 'mapy':
      /* §9.512, owner request - Surany asked for this one. **A search at the point, not a route start.** Mapy's
       * routing deep link needs an origin as well and its parameter form is not something to guess at when a wrong
       * guess is a driver at the wrong door; this opens the map on the destination with a pin, and navigation is one
       * tap from there. Owner question #85 asks whether that tap is worth removing.
       *
       * Latitude first here, the opposite of Sygic two lines up - which is why each has its own case and its own
       * test rather than sharing a branch. */
      return `https://mapy.com/zakladni?q=${lat},${lon}`
    case 'google':
      return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`
  }
}

/**
 * The link for one app and an address nobody could resolve - §9.507.
 *
 * **Sygic falls back to Google deliberately.** Its coordinate scheme cannot search by text, so the alternatives
 * are a Google URL that opens a map or a Sygic URL that does nothing at all. A silent no-op is the failure this
 * entry exists to avoid.
 *
 * The text **is** encoded here, unlike the pair: it is a query string and a comma in a street name belongs to it.
 */
function addressUrl(app: MapApp, address: string): string {
  const encoded = encodeURIComponent(address)
  switch (app) {
    case 'apple':
      return `https://maps.apple.com/?daddr=${encoded}&dirflg=d`
    case 'waze':
      return `https://waze.com/ul?q=${encoded}&navigate=yes`
    case 'mapy':
      /* §9.512 - Mapy searches by text perfectly well, so unlike Sygic this needs no fallback to Google. */
      return `https://mapy.com/zakladni?q=${encoded}`
    case 'google':
    case 'sygic':
      return `https://www.google.com/maps/dir/?api=1&destination=${encoded}`
  }
}

/**
 * The franchise's chosen map, off a settings payload whose generated type does not declare it - §9.518.
 *
 * **The server sends it and the snapshot predates it.** §10A.260 added `mapApp` to `GET /settings/delivery`
 * and validates it against the five; `docs/openapi-v1.json` is captured from a running backend and has not
 * been re-exported, so `DeliverySettings` omits the field. §9.509 hit the same wall on the write and solved
 * it with a named variable.
 *
 * **Read defensively rather than with a cast**, and copied from the writer rather than from the interface -
 * §3.122's rule, whose defect was a reader inventing a key. `delivery()` answers a non-empty string from
 * `MAP_APPS`, so anything else means *the field is not there yet* and `null` is the honest answer: no
 * preference, and `chosenMapApp` falls back to Google.
 */
/**
 * Which map to open: the driver's own choice, then the franchise's, then Google - §9.519, owner O30.
 *
 * **One expression at one place, which FB-801's fix is what made possible.** While the choice was defaulted
 * *inside* `navigationUrl`, three defaults competed and none of them could see the employee; now the call site
 * decides and the builder only builds.
 *
 * **Null at each step means *no preference* rather than *use Google***, so this is a chain and not a race:
 * a driver who has chosen nothing follows their franchise, and a franchise that has chosen nothing gets the
 * default `chosenMapApp` already applies.
 */
export function preferredMapApp(employee: string | null, franchise: string | null): string | null {
  const own = (employee ?? '').trim()
  if (own !== '') {
    return own
  }
  const theirs = (franchise ?? '').trim()
  return theirs === '' ? null : theirs
}

/**
 * Which map the *Navigovať* button will open, said on the screen that opens it - §9.639, FB-965.
 *
 * **The franchise branch is the one that carries information.** A driver who has chosen nothing follows the
 * franchise, and from the driving surface that answer was invisible: the select reads *Podľa franšízy* and
 * nothing said whether that means Google or Mapy.com. `preferredMapApp` has always resolved it correctly;
 * what was missing is a sentence saying what it resolved to.
 *
 * **The own-choice branch is confirmation rather than information**, and it is here deliberately: after a
 * write the driver reads back the app their tap selected, which is the cheapest evidence a one-key patch
 * landed. The select's own label says the same thing, and a note that disappears on five of six values is a
 * note nobody learns to read.
 *
 * **`mapAppCaveat` on either branch**, because Sygic opens nothing at all on a phone without it installed
 * (§9.509) - a driver choosing it on their own screen is told what an operator choosing it is told.
 */
export function mapChoiceNote(employee: string | null, franchise: string | null): string {
  const own = (employee ?? '').trim()
  const resolved = chosenMapApp(own === '' ? franchise : own)
  const opens =
    own === ''
      ? `${tt('Podľa franšízy - navigácia otvorí')} ${MAP_APP_LABELS[resolved]}.`
      : `${tt('Navigácia otvorí')} ${MAP_APP_LABELS[resolved]}.`
  const caveat = mapAppCaveat(resolved)
  return caveat === null ? opens : `${opens} ${caveat}`
}

/**
 * The body for *I want this map* - §9.639, FB-965.
 *
 * **A named return type rather than an object literal at the call site**, which is §9.509's rule:
 * `PatchAuthMeProfileBody` does not declare `mapApp`, so a fresh literal is an excess-property error while a
 * `Record` reaching the mutation is not. The field is real - `ProfileService.update` validates it through
 * `employeeMapApp` (§10A.275) - and only the committed snapshot is behind.
 *
 * **Null rather than an omitted key for the empty option**, the same reasoning `profilePayload` carries: the
 * server reads null as *follow the franchise*, and an omitted key would leave the old choice in place, so
 * *Podľa franšízy* would appear to do nothing.
 *
 * **One key and no others**, which is what makes this safe from the driving screen: `update` is
 * `body.containsKey` per field, so nothing this body omits is written. A payload that carried a blank
 * `firstName` alongside it would wipe a name - §9.616's class, avoided by sending the one thing being changed.
 */
export function mapAppPayload(chosen: string): Record<string, string | null> {
  return { mapApp: chosen === '' ? null : chosen }
}

export function mapAppOf(settings: unknown): string | null {
  if (typeof settings !== 'object' || settings === null) {
    return null
  }
  const chosen = (settings as { mapApp?: unknown }).mapApp
  return typeof chosen === 'string' && chosen.trim() !== '' ? chosen.trim() : null
}

export function navigationUrl(
  destination: NavigationTarget | string | null,
  /**
   * Which map to open, or `null` for *no preference* - §9.518, FB-801.
   *
   * **Required, and it was optional until a driver never got the map their franchise had chosen.** There
   * is exactly one call site in the tree and it passed nothing, so `chosenMapApp(undefined)` answered
   * `google` for every franchise while the select saved, round-tripped and validated. §9.480 wrote the
   * rule down after being bitten twice: *prefer a required parameter over an optional one plus a ratchet
   * whenever every call site is ours.*
   *
   * `null` rather than an omitted argument, because it says *I have no preference* out loud where an
   * absent one says nothing and defaults silently.
   */
  mapApp: string | null,
): string | null {
  // The old signature took a bare address. Kept working, because §9.504 is about where the link points
  // rather than about who calls it, and a caller passing a string still gets the text behaviour.
  const app = chosenMapApp(mapApp)
  if (destination === null || typeof destination === 'string') {
    const only = (destination ?? '').trim()
    if (only === '') return null
    return addressUrl(app, only)
  }
  const { deliveryLat, deliveryLon } = destination
  if (deliveryLat !== null && deliveryLon !== null) {
    return pointUrl(app, deliveryLat, deliveryLon)
  }
  const trimmed = (destination.customerAddress ?? '').trim()
  if (trimmed === '') return null
  return addressUrl(app, trimmed)
}

export function telUrl(phone: string | null): string | null {
  const trimmed = (phone ?? '').replace(/\s/g, '')
  if (trimmed === '') return null
  return `tel:${trimmed}`
}

/**
 * A short buzz to confirm a tap, where the browser has one.
 *
 * The RN app calls `expo-haptics` sixty-one times. `navigator.vibrate` is the web's equivalent and **iOS
 * Safari does not implement it at all** — so this is best-effort by design, and every action it accompanies
 * also changes something visible. A driver on an iPhone must not be relying on a buzz that never comes.
 */
export function buzz(navigatorLike: { vibrate?: (pattern: number | number[]) => boolean }): boolean {
  if (typeof navigatorLike.vibrate !== 'function') return false
  try {
    return navigatorLike.vibrate(15)
  } catch {
    return false
  }
}

// ─── The wallet ──────────────────────────────────────────────────────────────

export interface Wallet {
  name: string
  isAvailable: boolean
  vehicleId: number | null
  vehicleName: string | null
  lifetimeOrders: number
  lifetimeCashCents: number
  lifetimeCardCents: number
  lifetimeTotalCents: number
}

export function walletOf(payload: unknown): Wallet | null {
  if (payload === null || payload === undefined || typeof payload !== 'object') return null
  const row = payload as Record<string, unknown>
  const vehicle = (row.vehicle ?? {}) as Record<string, unknown>
  return {
    name: text(row.name) ?? '',
    isAvailable: row.isAvailable === true,
    vehicleId: typeof row.vehicleId === 'number' ? row.vehicleId : null,
    vehicleName: text(vehicle.name),
    lifetimeOrders: count(row.orderCount),
    lifetimeCashCents: euros(row.cashTotal),
    lifetimeCardCents: euros(row.cardTotal),
    lifetimeTotalCents: euros(row.walletTotal),
  }
}

/**
 * What the wallet figures actually are.
 *
 * `walletTotal` is every order this driver has ever delivered — 40 045,35 € over 1 841 orders for one of
 * them. Only the cash half was ever in anybody's pocket, and none of it is owed today. Legacy's strip shows
 * a day; this endpoint has no window at all, so the surface says which is which rather than letting a
 * lifetime figure sit under a heading that reads like a debt.
 */
export function walletNote(wallet: Wallet): string {
  return `${tt('Za celý čas')}: ${formatCount(wallet.lifetimeOrders)} ${tt('rozvozov')}, ${tt('z toho v hotovosti')} ${formatCents(wallet.lifetimeCashCents)}. ${tt('Nie je to suma, ktorú máte teraz pri sebe.')}`
}

export function shiftLabel(wallet: Wallet): string {
  return wallet.isAvailable ? tt('Na smene') : tt('Mimo smeny')
}

/** Who is looking at the board: the role `/auth/me` names, and the mark *Jazdí aj rozvoz* (`alsoDrivesOf`). */
export interface BoardViewer {
  systemRole: string | null | undefined
  alsoDrives: boolean
}

/**
 * Why the board is empty, as a reason the screen can be asked about - §9.759.
 *
 * - `never-assigned` - **the dispatcher will never give this person a delivery**, FB-1078. Only a driver is on the
 *   dispatcher's picker - `Drivers.drivesSql` is the role Šofér or the mark *Jazdí aj rozvoz*, the rule `drives`
 *   spells here - while the screen opens to every administrator, because its endpoints answer them. So an
 *   administrator who is not marked must be told that, rather than promised an order that cannot come. A driver or
 *   a marked person never reads it; **an unknown role is not a no**, so a session that has not said who it is gets
 *   the ordinary note.
 * - `off-shift` - the driver is off their shift, so nothing can be assigned until they go on it.
 * - `waiting` - nothing is out yet. `my-deliveries` is `status IN ('on_the_way', 'at_address')`, so it holds an
 *   order only while it is out: assignment happens on `/dispatch` and the order arrives here already moving.
 *   There is no *ready to pick up* list, because in the whole dataset **no order has ever been `ready` with a
 *   driver on it**.
 *
 * `never-assigned` is asked first: going on shift changes nothing for somebody the picker never offers.
 */
export type EmptyBoardReason = 'never-assigned' | 'off-shift' | 'waiting'

export function emptyBoardReason(wallet: Wallet | null, viewer: BoardViewer): EmptyBoardReason {
  const role = (viewer.systemRole ?? '').trim()
  if (role !== '' && !drives(role, viewer.alsoDrives)) return 'never-assigned'
  if (wallet !== null && !wallet.isAvailable) return 'off-shift'
  return 'waiting'
}

/** What the empty board says, for each reason - exhaustive, so a fourth reason is a compile error, not a blank. */
export function emptyBoardNote(reason: EmptyBoardReason): string {
  switch (reason) {
    case 'never-assigned':
      return tt(
        'Objednávky na rozvoz vám dispečer nepridelí - nie ste označený ako rozvozár. „Jazdí aj rozvoz“ zaškrtne hlavný administrátor v Zamestnancoch.',
      )
    case 'off-shift':
      return tt('Ste mimo smeny, takže vám dispečer nemôže nič prideliť. Prepnite sa na smenu vyššie.')
    case 'waiting':
      return tt('Zatiaľ nič. Objednávka sa tu objaví, keď vám ju dispečer pridelí a vy vyrazíte.')
  }
}

// ─── The vehicle a driver is on ──────────────────────────────────────────────

export interface Vehicle {
  id: number
  name: string
  licensePlate: string | null
}

export function vehiclesOf(payload: unknown): Vehicle[] {
  const rows = Array.isArray(payload) ? payload : ((payload as { data?: unknown })?.data ?? [])
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    if (typeof row.id !== 'number') return []
    return [
      {
        id: row.id,
        name: text(row.name)?.trim() ?? String(row.id),
        licensePlate: text(row.licensePlate)?.trim() ?? null,
      },
    ]
  })
}

export function vehicleLabel(vehicle: Vehicle): string {
  return vehicle.licensePlate === null ? vehicle.name : `${vehicle.name} · ${vehicle.licensePlate}`
}

/**
 * Why the vehicle matters, said where a driver picks one.
 *
 * `/trip-log` and `/delivery-statistics` attribute distance and fuel by vehicle, so a driver left on last
 * week's van puts this week's diesel on it. The figure nobody checks is the one that quietly drifts.
 */
export function vehicleNote(current: Vehicle | null): string {
  if (current === null) {
    return tt('Nemáte vybrané vozidlo - kilometre a nafta sa nemajú k čomu priradiť.')
  }
  return `${tt('Jazdíte na')} ${vehicleLabel(current)}. ${tt('Podľa toho sa priradia kilometre aj spotreba, tak to prepnite, keď si vymeníte auto.')}`
}

export function currentVehicle(wallet: Wallet | null, vehicles: readonly Vehicle[]): Vehicle | null {
  if (wallet?.vehicleId == null) return null
  return vehicles.find((vehicle) => vehicle.id === wallet.vehicleId) ?? null
}

/**
 * Whether this order can be handed back - §9.370, FB-658.
 *
 * **The server's own condition, spelled the same way.** `OrderStatusService.IN_THE_FIELD` is
 * `{on_the_way, at_address}` and it is what lets a **driver** move an order to `quality_check` - a
 * transition otherwise reserved for `main_admin`, `admin` and `cook`. Anywhere else the server
 * refuses, so the button is **absent** rather than offered and refused: a driver at somebody's door
 * should not be reading a permission error.
 *
 * Restated here rather than inferred from *anything not delivered*, because that would offer it on
 * an order still at the counter, which the server would refuse.
 */
export function mayHandBack(status: string | undefined): boolean {
  return status === 'on_the_way' || status === 'at_address'
}

/**
 * What the status log will read months later - §9.370.
 *
 * Through `tt` like every other sentence: the reason is `@NotBlank` on the server and ends up on a
 * screen somebody reads, so it is a translated string rather than a constant with diacritics in it.
 */
export function handBackReason(): string {
  return tt('Zákazník neprevzal')
}

export function handBackNote(orderNumber: string | undefined): string {
  return `#${orderNumber ?? ''} · ${tt('Kuchyňa a dispečer to uvidia. Jedlo zostáva u vás, kým nepovedia inak.')}`
}

// ─── Ísť na adresu, then Doručená - §9.682, FB-1000 ──────────────────────────

/**
 * Which of the card's two steps an order is at - §9.682, owner #23b.
 *
 * **Legacy's card had one button whose action followed the status** (`deliveries.tsx:273-280`): *Ísť na
 * adresu* on `on_the_way`, *Doručiť* on `at_address`. The port kept only the second, so nothing wrote
 * `at_address` and the on-the-way text the owner edits on the SMS screen never left (§10A.341). The owner:
 * *"dal že na cestu a potom až doručená"* - set off, and **only then** delivered.
 *
 * **Only `on_the_way` asks for the first step.** Every other status offers the close, so an order in a state
 * this screen did not expect - a dispatcher's own *Na adrese*, or something newer than this file - is never
 * left without a way out; the server decides whether the close is allowed.
 *
 * Two named answers rather than a boolean, so the card reads as the sequence it draws.
 */
export type DoorStep = 'go' | 'deliver'

export function doorStep(status: string): DoorStep {
  return status === 'on_the_way' ? 'go' : 'deliver'
}

/**
 * What *Ísť na adresu* puts on the wire - §9.682.
 *
 * **Exactly legacy's body** (`deliveries.tsx:758`, `{ status: newStatus }` to `PATCH /orders/:id`), and one
 * key: the edit endpoint writes only the names a body carries, so a driver tapping this cannot touch anything
 * else on the order. A function rather than a literal at the call site, so the wire is asserted by a test
 * rather than retyped.
 */
export function goingToAddressPayload(): { status: 'at_address' } {
  return { status: 'at_address' }
}

// ─── The way into the rest of the system - §9.684, owner #9 ──────────────────

/**
 * Where a driver can go from the driving surface, and what lets them.
 *
 * The owner: *"v rozvozovej aplikácii hornú listu kde sa vie preklikávať v systéme dať nižšie lebo je to úplne
 * v Rohu a ťažko sa nato kliKa ( oni máju rozvozarov ktorý majú aj prehľady aj rozvoz nástenka ... )"*. The
 * header ended in an icon-only *Aplikácia* to `/dashboard`, beside *Odhlásiť*, gated on a role list rather than
 * on the permission the route asks - and there was no way to *Rozvoz - nástenka* at all.
 *
 * **Each exit carries the permission its own route declares** (`/dashboard` is `permission: 'dashboard'`,
 * `/dispatch` is `permission: 'dispatch'`), so a driver is never offered a link the router would answer with
 * its lock panel. `driving-exits-agree.test.ts` holds the two lists together.
 */
export interface DrivingExit {
  key: 'dashboard' | 'dispatch'
  path: string
  permission: string
  label: string
}

export function drivingExits(hasPermission: (permission: string) => boolean): DrivingExit[] {
  const exits: DrivingExit[] = [
    { key: 'dashboard', path: '/dashboard', permission: 'dashboard', label: tt('Prehľad') },
    { key: 'dispatch', path: '/dispatch', permission: 'dispatch', label: tt('Rozvoz - nástenka') },
  ]
  return exits.filter((exit) => hasPermission(exit.permission))
}

